import { readStudioEnv } from "@/services/cogo-platform-config"

/**
 * Where the AG-UI SSE runtime is, or the fact that this build does not run one.
 *
 * ── WHY THIS IS NOT `resolveChatGatewayBase` ──────────────────────────────
 *
 * Chat has two transports and they are two different services:
 *
 *   • the CHAT GATEWAY — a BASE that `gateway-chat-service.ts:113` appends
 *     `/api/chat-gateway` to. Resolved by `resolveChatGatewayBase`.
 *   • the AG-UI SSE RUNTIME — a WHOLE URL that `runSSEAdapter`
 *     (`chat-model-adapter.ts`) POSTs AG-UI frames to, appending nothing.
 *
 * Both were spelled from `VITE_CHAT_GATEWAY_URL`. That is only ever coherent
 * when the value is falsy, which is what hid it: while `.env.appliance`
 * declared the key empty, `getRuntimeUrl()` returned `''` and the SSE branch
 * was never taken. #2625 set it to `/api/platform` because the appliance
 * really does route `/api/platform/api/chat-gateway` — right for the gateway,
 * and it handed the SSE consumer a base it then POSTed to bare. Measured live
 * 2026-08-26: `POST /api/platform -> TypeError: Failed to fetch`, and no
 * `/api/chat-gateway` request at all, because `runSSEAdapter` returns before
 * `runChatServiceAdapter` is reached. The gateway that had just been wired up
 * was bypassed by the same string that wired it.
 *
 * No value satisfies both readings. A base and a whole URL are different
 * facts, so they get different names.
 *
 * ── WHY `VITE_COPILOTKIT_URL` AND NOT A NEW NAME ──────────────────────────
 *
 * This is not a new env var. `VITE_COPILOTKIT_URL` is the AG-UI runtime's own
 * name, already declared in `.env.example`, and `getRuntimeUrl()` already read
 * it — as the SECOND half of an `||`, where the first half shadowed it on
 * every deployment that set a gateway. Removing the shadow is the whole fix.
 *
 * ── WHY `null` IS NOT A REFUSAL HERE ──────────────────────────────────────
 *
 * Unlike `resolveChatGatewayBase`, `null` is not "this build cannot chat". It
 * means "this build has no SSE runtime", and the turn goes down the gateway
 * transport, which that build does have. Only when BOTH answer `null` is
 * there nothing to send with, and `resolveChatGatewayBase` already owns
 * saying so.
 */
export interface ChatRuntimeEnv {
  VITE_COPILOTKIT_URL?: string
}

/**
 * What the creator reads when the declared SSE runtime cannot be reached.
 *
 * `runSSEAdapter`'s fetch had no `catch`, so `TypeError: Failed to fetch`
 * rejected the async generator. assistant-ui marks the message
 * `incomplete/error` and rethrows; its `Thread` renders no error component for
 * that state and `studio-composer.tsx` does not await the `append` it rejects.
 * The result, measured live 2026-08-26, was a send that produced no error, no
 * notice and no bubble.
 *
 * Addressed to a creator, not an integrator: `TypeError: Failed to fetch` is
 * not something they can act on, and the second sentence is the move they can
 * make. Same shape as `CHAT_GATEWAY_UNCONFIGURED_SENTENCE` next door, and kept
 * local for the reason that one states.
 */
export const CHAT_RUNTIME_UNREACHABLE_SENTENCE =
  "채팅 런타임에 연결하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 보내 주세요."

/**
 * What the creator reads when the runtime answered, and refused.
 *
 * Replaces the bare `Error: ${res.status}` the SSE branch used to yield. The
 * status is KEPT because it is the one part an operator asked for a screenshot
 * of can act on, and it is the same shape `agent-reply-copy.ts` already uses
 * for the A2A lane (`봇에 도달하지 못했습니다 (HTTP …)`).
 */
export function chatRuntimeRefusedSentence(status: number): string {
  return `채팅 런타임이 요청을 처리하지 못했습니다 (HTTP ${status}). 잠시 후 다시 보내 주세요.`
}

/**
 * The URL to POST AG-UI frames to, or `null` when this build runs no SSE
 * runtime.
 *
 * `env` accepts a loose record on purpose: the gate in
 * `__tests__/chat-transport-separation.test.ts` passes the GATEWAY key in
 * here to prove it is ignored. A narrow parameter type would make that
 * re-merge uncompilable rather than red, and a compile error in a test is a
 * weaker signal than a named failing assertion.
 */
export function resolveChatRuntimeUrl(
  env?: ChatRuntimeEnv & Record<string, string | undefined>,
): string | null {
  const declared =
    env !== undefined
      ? env.VITE_COPILOTKIT_URL
      : readStudioEnv("VITE_COPILOTKIT_URL")

  if (declared === undefined) return null
  const explicit = declared.trim().replace(/\/+$/, "")
  return explicit.length > 0 ? explicit : null
}
